import React, {
  useState,
  useRef,
} from "react";

import { useNavigate } from "react-router-dom";

import "./UserName.css";

export default function UserName() {

  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");

  const [profileImage, setProfileImage] =
    useState(null);

  const [compressedProfileFile, setCompressedProfileFile] =
    useState(null);

  const [loading, setLoading] = useState(false);

  // =====================================================
  // LOADING POPUP STATE (NEW)
  // =====================================================

  const [loadingMessage, setLoadingMessage] =
    useState("");

  const [loadingPopupVisible, setLoadingPopupVisible] =
    useState(false);

  const fileInputRef = useRef(null);


  /* =====================================================
     CAMERA
  ====================================================== */

  const handleCameraClick = () => {

    if (!loading) {

      fileInputRef.current?.click();

    }

  };


  /* =====================================================
     IMAGE COMPRESSION  →  TARGET: 10KB MAX
  ====================================================== */

  const compressImageTo10KB = (
    file,
    maxSizeKB = 10
  ) => {

    return new Promise((resolve, reject) => {

      const reader = new FileReader();


      reader.onload = (event) => {

        const img = new Image();


        img.onload = () => {

          try {

            const canvas =
              document.createElement("canvas");

            const ctx =
              canvas.getContext("2d");


            if (!ctx) {

              reject(
                new Error(
                  "Image compression is not supported."
                )
              );

              return;

            }


            let width = img.width;

            let height = img.height;


            /* =================================================
               LARGE IMAGE RESIZE (initial)
            ================================================= */

            const MAX_DIMENSION = 1600;

            const MIN_DIMENSION = 200;


            if (
              width > MAX_DIMENSION ||
              height > MAX_DIMENSION
            ) {

              if (width > height) {

                height =
                  (height / width) *
                  MAX_DIMENSION;

                width =
                  MAX_DIMENSION;

              } else {

                width =
                  (width / height) *
                  MAX_DIMENSION;

                height =
                  MAX_DIMENSION;

              }

            }


            canvas.width =
              Math.round(width);

            canvas.height =
              Math.round(height);


            /* =================================================
               WHITE BACKGROUND
            ================================================= */

            ctx.fillStyle = "#ffffff";

            ctx.fillRect(
              0,
              0,
              canvas.width,
              canvas.height
            );


            ctx.drawImage(
              img,
              0,
              0,
              canvas.width,
              canvas.height
            );


            /* =================================================
               TARGET SIZE (10KB)
            ================================================= */

            const TARGET_BYTES =
              maxSizeKB * 1024;


            let quality = 0.9;

            const MIN_QUALITY = 0.05;

            const QUALITY_STEP = 0.05;

            const DIMENSION_STEP = 0.85;


            /* =================================================
               RECURSIVE COMPRESSION
            ================================================= */

            const tryCompression = () => {

              canvas.toBlob(
                (blob) => {

                  if (!blob) {

                    reject(
                      new Error(
                        "Unable to compress image."
                      )
                    );

                    return;

                  }


                  /* =========================================
                     SUCCESS — IMAGE <= 10KB
                  ========================================= */

                  if (
                    blob.size <=
                    TARGET_BYTES
                  ) {

                    const compressedFile =
                      new File(
                        [blob],
                        "profile-image.jpg",
                        {
                          type: "image/jpeg",
                          lastModified:
                            Date.now(),
                        }
                      );


                    resolve(
                      compressedFile
                    );

                    return;

                  }


                  /* =========================================
                     STEP 1: REDUCE QUALITY
                  ========================================= */

                  quality -= QUALITY_STEP;


                  if (
                    quality >=
                    MIN_QUALITY
                  ) {

                    tryCompression();

                    return;

                  }


                  /* =========================================
                     STEP 2: REDUCE DIMENSIONS
                  ========================================= */

                  const newWidth =
                    Math.round(
                      canvas.width *
                      DIMENSION_STEP
                    );

                  const newHeight =
                    Math.round(
                      canvas.height *
                      DIMENSION_STEP
                    );


                  /* ---------- FINAL FLOOR ---------- */

                  if (
                    newWidth < MIN_DIMENSION ||
                    newHeight < MIN_DIMENSION
                  ) {

                    canvas.toBlob(
                      (finalBlob) => {

                        if (!finalBlob) {

                          reject(
                            new Error(
                              "Unable to compress image."
                            )
                          );

                          return;

                        }


                        if (
                          finalBlob.size >
                          TARGET_BYTES
                        ) {

                          reject(
                            new Error(
                              `Image could not be compressed below ${maxSizeKB}KB. Please choose a simpler image.`
                            )
                          );

                          return;

                        }


                        const finalFile =
                          new File(
                            [finalBlob],
                            "profile-image.jpg",
                            {
                              type: "image/jpeg",
                              lastModified:
                                Date.now(),
                            }
                          );


                        resolve(
                          finalFile
                        );

                      },
                      "image/jpeg",
                      MIN_QUALITY
                    );

                    return;

                  }


                  /* ---------- RESIZE CANVAS ---------- */

                  canvas.width =
                    newWidth;

                  canvas.height =
                    newHeight;


                  ctx.fillStyle =
                    "#ffffff";

                  ctx.fillRect(
                    0,
                    0,
                    canvas.width,
                    canvas.height
                  );


                  ctx.drawImage(
                    img,
                    0,
                    0,
                    canvas.width,
                    canvas.height
                  );


                  quality = 0.85;


                  tryCompression();

                },
                "image/jpeg",
                quality
              );

            };


            tryCompression();

          } catch (error) {

            reject(error);

          }

        };


        img.onerror = () => {

          reject(
            new Error(
              "Unable to read selected image."
            )
          );

        };


        img.src =
          event.target.result;

      };


      reader.onerror = () => {

        reject(
          new Error(
            "Unable to read image file."
          )
        );

      };


      reader.readAsDataURL(file);

    });

  };


  /* =====================================================
     IMAGE CHANGE HANDLER
  ====================================================== */

  const handleImageChange = async (e) => {

    const file =
      e.target.files?.[0];


    if (!file) {

      return;

    }


    if (
      !file.type.startsWith("image/")
    ) {

      alert(
        "Please select a valid image."
      );

      e.target.value = "";

      return;

    }


    try {

      setLoading(true);

      // NEW: show loading popup
      setLoadingMessage("Compressing your photo...");

      setLoadingPopupVisible(true);


      console.log(
        "================================="
      );

      console.log("ORIGINAL IMAGE");

      console.log("File name:", file.name);

      console.log("File type:", file.type);

      console.log(
        "Original size:",
        (file.size / (1024 * 1024)).toFixed(2),
        "MB"
      );


      const compressedFile =
        await compressImageTo10KB(file, 10);


      console.log(
        "================================="
      );

      console.log("COMPRESSED IMAGE");

      console.log(
        "Compressed size:",
        (compressedFile.size / 1024).toFixed(2),
        "KB"
      );


      if (
        compressedFile.size >
        10 * 1024
      ) {

        alert(
          "Image compression failed. Please select another image."
        );

        e.target.value = "";

        setCompressedProfileFile(null);

        setProfileImage(null);

        return;

      }


      setCompressedProfileFile(compressedFile);


      const imageUrl =
        URL.createObjectURL(compressedFile);


      setProfileImage(imageUrl);


      console.log("Image ready for upload.");

    } catch (error) {

      console.error(
        "IMAGE COMPRESSION ERROR:",
        error
      );


      alert(
        error.message ||
        "Image compress nahi ho payi."
      );


      e.target.value = "";

      setCompressedProfileFile(null);

      setProfileImage(null);


    } finally {

      setLoading(false);

      // NEW: hide loading popup
      setLoadingPopupVisible(false);

      setLoadingMessage("");

    }

  };


  /* =====================================================
     SAVE NAME + PROFILE IMAGE
  ====================================================== */

  const handleSave = async (e) => {

    e.preventDefault();


    const trimmedName =
      fullName.trim();


    if (!trimmedName) {

      alert(
        "Please enter your full name."
      );

      return;

    }


    if (loading) {

      return;

    }


    setLoading(true);

    // NEW: show loading popup
    setLoadingMessage("Saving your profile...");

    setLoadingPopupVisible(true);


    try {

      const userId =
        localStorage.getItem("user_id");


      const deviceUUID =
        localStorage.getItem("device_uuid");


      console.log("USER ID:", userId);

      console.log("DEVICE UUID:", deviceUUID);


      if (!userId) {

        alert(
          "User ID not found. Please restart registration."
        );

        return;

      }


      if (!deviceUUID) {

        alert(
          "Device UUID not found. Please restart registration."
        );

        return;

      }


      const selectedFile =
        compressedProfileFile || null;


      if (
        selectedFile &&
        selectedFile.size > 10 * 1024
      ) {

        alert(
          "Profile image must be 10KB or less."
        );

        return;

      }


      const formData = new FormData();


      formData.append("user_id", userId);

      formData.append("device_uuid", deviceUUID);

      formData.append("name", trimmedName);


      if (selectedFile) {

        formData.append(
          "profile_image",
          selectedFile
        );

      }


      const response =
        await fetch(
          "https://coinyoco.world/brobizee-api/update_username.php",
          {
            method: "POST",
            body: formData
          }
        );


      const responseText =
        await response.text();


      console.log(
        "RAW PHP RESPONSE:",
        responseText
      );


      if (!response.ok) {

        throw new Error(
          `HTTP ${response.status}: ${responseText}`
        );

      }


      let result;


      try {

        result =
          JSON.parse(responseText);

      } catch (jsonError) {

        console.error(
          "PHP returned invalid JSON:",
          responseText
        );


        throw new Error(
          "PHP API ne valid JSON return kiya."
        );

      }


      console.log("USERNAME API RESULT:", result);


      if (result.success) {

        localStorage.setItem(
          "user_name",
          result.name || trimmedName
        );


        if (result.profile_user) {

          localStorage.setItem(
            "profile_user",
            result.profile_user
          );

        }


        const savedUserId =
          localStorage.getItem("user_id");


        const savedDeviceUUID =
          localStorage.getItem("device_uuid");


        if (!savedUserId || !savedDeviceUUID) {

          alert(
            "User ID or Device ID not found."
          );

          return;

        }


        console.log("PROFILE SAVED SUCCESSFULLY");


        navigate(
          `/${savedUserId}/${savedDeviceUUID}/userandpartner`
        );


        return;

      }


      alert(
        result.message ||
        "Name/profile save nahi hua."
      );


    } catch (error) {

      console.error("USERNAME API ERROR:", error);


      alert(
        `Server/API Error:\n${error.message}`
      );


    } finally {

      setLoading(false);

      // NEW: hide loading popup
      setLoadingPopupVisible(false);

      setLoadingMessage("");

    }

  };


  /* =====================================================
     NAME VALIDATION
  ====================================================== */

  const isNameValid =
    fullName.trim().length > 0;


  /* =====================================================
     UI
  ====================================================== */

  return (

    <div className="username-page-wrapper">

      <div className="username-app-card">


        {/* AMBIENT */}

        <div className="username-ambient username-ambient-one"></div>

        <div className="username-ambient username-ambient-two"></div>


        {/* HEADER */}

        <header className="username-header">

          <button
            type="button"
            className="username-back-btn"
            aria-label="Go Back"
            onClick={() =>
              navigate(-1)
            }
            disabled={loading}
          >

            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >

              <path d="M15 18l-6-6 6-6" />

            </svg>

          </button>


          <div className="username-progress">

            <span className="username-progress-dot"></span>

            <span className="username-progress-dot"></span>

            <span className="username-progress-dot active"></span>

            <span className="username-progress-dot"></span>

          </div>

        </header>


        {/* MAIN */}

        <main className="username-content">

          <span className="username-eyebrow">

            YOUR PROFILE

          </span>


          <h1 className="username-title">

            What's your

            <span>
              {" "}full name?
            </span>

          </h1>


          <p className="username-subtitle">

            Tell us your name so we can personalize
            your BroBizee experience.

          </p>


          {/* AVATAR */}

          <div className="avatar-section">

            <div
              className={`avatar-circle ${
                profileImage
                  ? "has-image"
                  : ""
              }`}
            >

              {profileImage ? (

                <img
                  src={profileImage}
                  alt="Profile"
                  className="avatar-img"
                />

              ) : (

                <svg
                  className="default-avatar-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                >

                  <circle
                    cx="12"
                    cy="8"
                    r="3.5"
                  />

                  <path
                    d="M5 20c.8-4 3.1-6 7-6s6.2 2 7 6"
                  />

                </svg>

              )}


              <button
                type="button"
                className="avatar-camera-badge"
                onClick={
                  handleCameraClick
                }
                disabled={loading}
              >

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >

                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />

                  <circle
                    cx="12"
                    cy="13"
                    r="4"
                  />

                </svg>

              </button>

            </div>


            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="user"
              onChange={
                handleImageChange
              }
              className="hidden-file-input"
            />


            <button
              type="button"
              className="open-camera-btn"
              onClick={
                handleCameraClick
              }
              disabled={loading}
            >

              <span className="camera-icon">

                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >

                  <path d="M23 19a2 2 0 0 1-2-2V8a2 2 0 0 0-2-2h-4l-2-3h-6L5 6H3a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2z" />

                  <circle
                    cx="12"
                    cy="13"
                    r="4"
                  />

                </svg>

              </span>


              <span>

                {profileImage
                  ? "Change Photo"
                  : "Add Profile Photo"}

              </span>

            </button>


            <span className="photo-hint">

              Optional · JPG, PNG, WEBP · Auto compressed to 10KB

            </span>

          </div>


          {/* FORM */}

          <form
            onSubmit={handleSave}
            className="username-form"
          >

            <div className="name-input-wrapper">

              <span className="input-label">

                Full Name

              </span>


              <div className="name-input-container">

                <svg
                  className="name-input-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >

                  <circle
                    cx="12"
                    cy="8"
                    r="3"
                  />

                  <path
                    d="M5 20c.8-4 3.1-6 7-6s6.2 2 7 6"
                  />

                </svg>


                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) =>
                    setFullName(
                      e.target.value
                    )
                  }
                  className="name-input"
                  autoComplete="name"
                  required
                  disabled={loading}
                />


                {fullName.trim() &&
                  !loading && (

                    <button
                      type="button"
                      className="clear-name-btn"
                      onClick={() =>
                        setFullName("")
                      }
                      aria-label="Clear name"
                    >

                      ×

                    </button>

                  )}

              </div>

            </div>


            {/* =================================================
                SAVE BUTTON — text no longer changes,
                only disabled state toggles
            ================================================= */}

            <button
              type="submit"
              className={`save-btn ${
                isNameValid
                  ? "enabled"
                  : ""
              }`}
              disabled={
                !isNameValid ||
                loading
              }
            >

              <span>Continue</span>


              <span className="save-arrow">

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >

                  <path d="M5 12h14" />

                  <path d="M13 6l6 6-6 6" />

                </svg>

              </span>

            </button>

          </form>

        </main>


        {/* FOOTER */}

        <footer className="username-footer">

          <img
            src="/images/logo.png"
            alt="BroBizee Logo"
            className="brand-logo-img"
          />

          <span>

            Everything You Need, Ends With BroBizee.

          </span>

        </footer>


        {/* =================================================
            LOADING POPUP (NEW)
        ================================================= */}

        {loadingPopupVisible && (

          <div className="username-loading-overlay">

            <div className="username-loading-card">

              <div className="username-loading-spinner">

                <svg
                  viewBox="0 0 50 50"
                  className="username-spinner-svg"
                >

                  <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke="#e6eef6"
                    strokeWidth="5"
                  />

                  <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke="#005CA9"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray="90 150"
                  />

                </svg>

              </div>


              <h3 className="username-loading-title">

                {loadingMessage || "Please wait..."}

              </h3>


              <p className="username-loading-subtitle">

                This may take a few seconds.

              </p>


              <div className="username-loading-dots">

                <span></span>
                <span></span>
                <span></span>

              </div>

            </div>

          </div>

        )}

      </div>

    </div>

  );

}